import { Collection, Database, Q } from '@nozbe/watermelondb';

import {
    ContactDBServiceOptions,
    DBServiceOptionsWithImagesAndValidation,
    DBServiceOptionsWithImagesLoggerAndValidation,
    EntryDBServiceOptions,
    EventDBServiceOptions,
    HorseDBServiceOptions,
} from '../../types/dbService';
import {
    EventModel,
    EventPayload,
    PreparedDataOnAdd,
    PreparedDataOnDelete,
    PreparedDataOnUpdate,
} from '../../types/Events';
import { getLocal, getNowISO } from '../../utils/date';
import { OperationContext } from '../../types/operations';
import { DatabaseValidation } from '../../types/validation';
import { createDefaultWriteStrategy } from '../operations/createDefaultWriteStrategy';
import { EventWriteStrategy } from '../../types/writeStrategy';
import {
    addContactsRelatedData,
    addEventUsersRelatedData,
    addHorsesRelatedData,
    addUserRelatedData,
    logDBActions,
    mapContactsRelatedData,
    mapHorsesRelatedData,
    prepareCreateEvent,
    prepareCreateLocation,
} from './helpers/event/eventAdd';
import {
    addFirebaseInvitations,
    clearEventNotificationCache,
    mapRecords,
    schedulePN,
} from './helpers/event/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logUpdateDBActions,
    mapUpdateContactsRelatedData,
    mapUpdateEntriesRelatedData,
    mapUpdateEventUsersRelatedData,
    mapUpdateHorsesRelatedData,
    mapUpdateUserRelatedData,
    prepareUpdateEvent,
    prepareUpdateLocation,
    updateContactsRelatedData,
    updateEntriesRelatedData,
    updateEventUsersRelatedData,
    updateHorsesRelatedData,
    updateUserRelatedData,
} from './helpers/event/eventUpdate';
import {
    deleteContactRemindersRelatedData,
    deleteContactsRelatedData,
    deleteEntriesRelatedData,
    deleteEventUsersRelatedData,
    deleteHorsesRelatedData,
    logDeleteDBActions,
    mapDeleteContactRemindersRelatedData,
    mapDeleteContactsRelatedData,
    mapDeleteEntriesRelatedData,
    mapDeleteEventUsersRelatedData,
    mapDeleteHorsesRelatedData,
    prepareDeleteEvent,
} from './helpers/event/eventDelete';

class Event {
    private database: Database;
    private collection: Collection<EventModel>;
    private table = 'events';
    private options: EventDBServiceOptions;
    private validation: DatabaseValidation;
    private writeStrategy: EventWriteStrategy;

    constructor(options: EventDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
        this.validation = options.validation;
        this.writeStrategy =
            options.writeStrategy ?? createDefaultWriteStrategy();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    async getFutureReminderEvents() {
        return this.collection
            .query(
                Q.where('user_reminder', Q.notEq(null)),
                Q.where('starts_time', Q.gt(getNowISO())),
            )
            .fetch();
    }

    getAttentionNeededEvents(organisationId: string, userID?: string) {
        const yesterdayDate = getLocal().minus({ days: 1 });

        const queryConditions: Q.Clause[] = [
            Q.experimentalJoinTables(['event_contacts', 'event_users']),
            Q.where('organisation_id', organisationId),
            Q.where('ends_time', Q.gt(yesterdayDate.toISO())),
            Q.on('event_contacts', Q.where('status', 'rejected')),
            Q.sortBy('starts_time', Q.asc),
        ];

        if (userID) {
            queryConditions.push(
                Q.on('event_users', Q.where('user_id', userID)),
            );
        }

        return this.collection.query(...queryConditions).observe();
    }

    getTodayAndTomorrowEvents(limit: number) {
        const today = getLocal();
        const tomorrow = today.plus({ days: 1 });

        return this.collection.query(
            Q.or(
                Q.where(
                    'starts_time',
                    Q.like(
                        `${Q.sanitizeLikeString(today.toISO().split('T')[0])}%`,
                    ),
                ),
                Q.where(
                    'starts_time',
                    Q.like(
                        `${Q.sanitizeLikeString(
                            tomorrow.toISO().split('T')[0],
                        )}%`,
                    ),
                ),
            ),
            Q.take(limit),
            Q.sortBy('starts_time', Q.asc),
        );
    }

    async add({
        payload,
        userId,
        userPayload,
        options,
    }: {
        payload: EventPayload;
        userId: string;
        userPayload?: {
            timezone?: string;
            isEntryCreationFromEventEnabled?: boolean;
        };
        options?: { operationContext?: OperationContext };
    }) {
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        };
        const serviceOptionsWithLogger: DBServiceOptionsWithImagesLoggerAndValidation =
            {
                database: this.database,
                imageService: this.options.imageService,
                logger: this.options.logger,
                validation: this.validation,
            };

        const {
            horses,
            contacts,
            userReminder,
            contactsReminder,
            contactsReminderTimeBefore,
            ownersReminder,
            membersIds,
            invitations,
        } = payload;

        const newEvent = await this.database.write(async () => {
            let preparedData: PreparedDataOnAdd[] = [];

            const { preparedLocation, existingLocation } =
                await prepareCreateLocation({
                    location: payload.location,
                    userId,
                    serviceOptions,
                });

            if (preparedLocation) {
                preparedData.push({
                    record: preparedLocation,
                    table: 'locations',
                    operation: 'add',
                });
            }

            const locationElement = preparedLocation || existingLocation;

            const createdEvent = await prepareCreateEvent({
                payload,
                userId,
                serviceOptions,
                locationElement,
                collection: this.collection,
            });
            preparedData.push({
                record: createdEvent,
                table: 'events',
                operation: 'add',
            });

            const horsesRelatedData = await addHorsesRelatedData({
                createdEvent,
                horses: horses ?? [],
                database: this.database,
                logDBAction: this.options.logger.logDBAction,
                tableName: 'events',
                payload,
                userId,
                membersIds,
                serviceOptions: serviceOptionsWithLogger,
            });
            preparedData = mapHorsesRelatedData(
                horsesRelatedData,
                preparedData,
            );

            const contactsRelatedData = await addContactsRelatedData({
                createdEvent,
                contacts,
                invitations,
                contactsReminderTimeBefore,
                contactsReminder,
                ownersReminder,
                userReminder,
                database: this.database,
                logDBAction: this.options.logger.logDBAction,
                tableName: 'events',
            });
            preparedData = mapContactsRelatedData(
                contactsRelatedData,
                preparedData,
            );

            const { eventUsersToAdd } = await addEventUsersRelatedData({
                createdEvent,
                membersIds,
                serviceOptions,
            });
            preparedData.push(
                ...mapRecords(eventUsersToAdd, 'event_users', 'add'),
            );

            const userToUpdate = await addUserRelatedData({
                userId,
                userPayload,
                serviceOptions,
            });

            if (userToUpdate) {
                preparedData.push({
                    record: userToUpdate,
                    table: 'users',
                    operation: 'update',
                });
            }

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await this.writeStrategy.commitPreparedData({
                preparedData,
                database: this.database,
                transactionAction: 'create',
                operationContext: options?.operationContext,
            });
            await schedulePN({
                createdEvent,
                userReminder,
                notificationsService: this.options.notificationsService,
            });
            logDBActions({
                locationElement,
                createdEvent,
                horsesRelatedData,
                contactsRelatedData,
                eventUsersToAdd,
                logDBAction: this.options.logger.logDBAction,
                tableName: 'events',
            });

            // Create firebase invitations after event is created
            // to avoid db sync during event creation

            if (this.options.firestoreService) {
                addFirebaseInvitations({
                    eventContactsToAdd: contactsRelatedData.eventContactsToAdd,
                    firestoreService: this.options.firestoreService,
                    organisationId: createdEvent.organisationId,
                    eventId: createdEvent.id,
                });
            }

            return createdEvent;
        });

        return newEvent;
    }

    async update({
        id,
        notifyContacts,
        payload,
        userId,
        operationContext,
    }: {
        id: string;
        notifyContacts?: boolean;
        payload: EventPayload;
        userId: string;
        operationContext?: OperationContext;
    }) {
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        };
        const horseServiceOptions: HorseDBServiceOptions = {
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        };
        const contactServiceOptions: ContactDBServiceOptions = {
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        };
        const entryServiceOptions: EntryDBServiceOptions = {
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        };

        const {
            contacts,
            contactsReminder,
            contactsReminderTimeBefore,
            horses,
            ownersReminder,
            userReminder,
            membersIds,
            invitations,
        } = payload;

        const eventElement = await this.getByID(id);

        const updatedEvent = await this.database.write(async () => {
            let preparedData: PreparedDataOnUpdate[] = [];

            const { preparedLocation, existingLocation } =
                await prepareUpdateLocation({
                    location: payload.location,
                    userId,
                    serviceOptions,
                });

            if (preparedLocation) {
                preparedData.push({
                    record: preparedLocation,
                    table: 'locations',
                    operation: 'add',
                });
            }

            const locationElement = preparedLocation || existingLocation;

            const updatedEventElement = await prepareUpdateEvent({
                eventElement,
                payload,
                notifyContacts,
                locationElement,
                serviceOptions,
                logger: this.options.logger,
            });

            preparedData.push({
                record: updatedEventElement,
                table: 'events',
                operation: 'update',
            });

            await clearEventNotificationCache({
                eventId: id,
                cacheService: this.options.cacheService,
                notificationsService: this.options.notificationsService,
            });

            const horsesRelatedData = await updateHorsesRelatedData({
                eventElement,
                horses,
                eventId: id,
                payload,
                serviceOptions: horseServiceOptions,
                database: this.database,
                validation: this.validation,
            });
            preparedData = mapUpdateHorsesRelatedData(
                horsesRelatedData,
                preparedData,
            );

            const contactsRelatedData = await updateContactsRelatedData({
                eventElement,
                eventId: id,
                contacts,
                contactsReminder,
                contactsReminderTimeBefore,
                ownersReminder,
                userReminder,
                invitations,
                notifyContacts,
                addNewHorsesToContact: payload.addNewHorsesToContact,
                newHorses: payload.newHorses,
                database: this.database,
                serviceOptions,
                contactServiceOptions,
            });
            preparedData = mapUpdateContactsRelatedData(
                contactsRelatedData,
                preparedData,
            );

            const entriesRelatedData = await updateEntriesRelatedData({
                eventId: id,
                eventStartsTime: payload.startsTime,
                userId,
                relatedEntriesToUpdate:
                    horsesRelatedData.relatedEntriesToUpdate,
                serviceOptions: entryServiceOptions,
            });
            preparedData = mapUpdateEntriesRelatedData(
                entriesRelatedData,
                preparedData,
            );

            const eventUsersRelatedData = await updateEventUsersRelatedData({
                membersIds,
                eventId: id,
                serviceOptions,
            });
            preparedData = mapUpdateEventUsersRelatedData(
                eventUsersRelatedData,
                preparedData,
            );

            const userRelatedData = await updateUserRelatedData({
                payload,
                userId,
                database: this.database,
            });
            preparedData = mapUpdateUserRelatedData(
                userRelatedData,
                preparedData,
            );

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await this.writeStrategy.commitPreparedData({
                preparedData,
                database: this.database,
                transactionAction: 'update',
                operationContext,
            });

            await schedulePN({
                createdEvent: updatedEventElement,
                userReminder,
                notificationsService: this.options.notificationsService,
            });

            logUpdateDBActions({
                eventElement,
                buckets: {
                    eventHorsesToAdd: horsesRelatedData.eventHorsesToAdd,
                    eventHorsesToDelete: horsesRelatedData.eventHorsesToDelete,
                    eventContactsToAdd: contactsRelatedData.eventContactsToAdd,
                    eventContactsToUpdate:
                        contactsRelatedData.eventContactsToUpdate,
                    eventContactsToDelete:
                        contactsRelatedData.eventContactsToDelete,
                    eventContactRemindersToAdd:
                        contactsRelatedData.eventContactRemindersToAdd,
                    eventContactRemindersToUpdate:
                        contactsRelatedData.eventContactRemindersToUpdate,
                    eventContactRemindersToDelete:
                        contactsRelatedData.eventContactRemindersToDelete,
                    relatedEntriesToUpdate:
                        entriesRelatedData.relatedEntriesToUpdate,
                    horsesToUpdate: horsesRelatedData.horsesToUpdate,
                    inventoryChangesToAdd:
                        entriesRelatedData.inventoryChangesToAdd,
                    eventUsersToAdd: eventUsersRelatedData.eventUsersToAdd,
                    eventUsersToDelete:
                        eventUsersRelatedData.eventUsersToDelete,
                    horseContactsToAdd: contactsRelatedData.horseContactsToAdd,
                    horseContactsToDelete:
                        contactsRelatedData.horseContactsToDelete,
                    userToUpdate: userRelatedData.userToUpdate,
                },
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            if (this.options.firestoreService) {
                addFirebaseInvitations({
                    eventContactsToAdd: contactsRelatedData.eventContactsToAdd,
                    firestoreService: this.options.firestoreService,
                    organisationId: eventElement.organisationId,
                    eventId: eventElement.id,
                });
            }

            return updatedEventElement;
        });

        return updatedEvent;
    }

    async deleteByID(
        id: string,
        userId: string,
        options?: { operationContext?: OperationContext },
    ) {
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        };
        const entryServiceOptions: EntryDBServiceOptions = {
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        };

        const eventElement = await this.getByID(id);

        await this.database.write(async () => {
            let preparedData: PreparedDataOnDelete[] = [];

            const preparedEvent = await prepareDeleteEvent({ eventElement });

            preparedData.push({
                record: preparedEvent,
                table: 'events',
                operation: 'delete',
            });

            await clearEventNotificationCache({
                eventId: id,
                cacheService: this.options.cacheService,
                notificationsService: this.options.notificationsService,
            });

            const horsesRelatedData = await deleteHorsesRelatedData({
                eventElement,
                eventId: id,
                serviceOptions,
            });
            preparedData = mapDeleteHorsesRelatedData(
                horsesRelatedData,
                preparedData,
            );

            const contactsRelatedData = await deleteContactsRelatedData({
                eventElement,
                eventId: id,
                serviceOptions,
            });
            preparedData = mapDeleteContactsRelatedData(
                contactsRelatedData,
                preparedData,
            );

            const contactRemindersRelatedData =
                await deleteContactRemindersRelatedData({
                    eventId: id,
                    serviceOptions,
                });
            preparedData = mapDeleteContactRemindersRelatedData(
                contactRemindersRelatedData,
                preparedData,
            );

            const entriesRelatedData = await deleteEntriesRelatedData({
                eventElement,
                eventId: id,
                userId,
                serviceOptions: entryServiceOptions,
            });
            preparedData = mapDeleteEntriesRelatedData(
                entriesRelatedData,
                preparedData,
            );

            const eventUsersRelatedData = await deleteEventUsersRelatedData({
                eventId: id,
                serviceOptions,
            });
            preparedData = mapDeleteEventUsersRelatedData(
                eventUsersRelatedData,
                preparedData,
            );

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await this.writeStrategy.commitPreparedData({
                preparedData,
                database: this.database,
                transactionAction: 'delete',
                operationContext: options?.operationContext,
            });

            logDeleteDBActions({
                eventElement,
                buckets: {
                    eventHorsesToDelete: horsesRelatedData.eventHorsesToDelete,
                    eventContactsToDelete:
                        contactsRelatedData.eventContactsToDelete,
                    eventContactRemindersToDelete:
                        contactRemindersRelatedData.eventContactRemindersToDelete,
                    entryProceduresToDelete:
                        entriesRelatedData.entryProceduresToDelete,
                    entryProductsToDelete:
                        entriesRelatedData.entryProductsToDelete,
                    entriesToDelete: entriesRelatedData.entriesToDelete,
                    relatedEntriesToUpdate:
                        entriesRelatedData.relatedEntriesToUpdate,
                    eventUsersToDelete:
                        eventUsersRelatedData.eventUsersToDelete,
                },
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        });
    }
}

export default Event;
